import { memo, useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  User,
  LogOut,
  Settings,
  MoreVertical,
  Sun,
  Moon,
  Languages,
  CreditCard,
} from 'lucide-react'
import { useTheme } from '../hooks/useTheme'
import { useAuth } from '../../features/auth/state/useAuth'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/app/routes'

const ICON_STROKE = 2

type UserProfileProps = {
  isCollapsed?: boolean
  onRequestExpand?: () => void
}

export const UserProfile = memo(function UserProfile({
  isCollapsed,
  onRequestExpand,
}: UserProfileProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const auth = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isCollapsed) {
      setIsOpen(false)
    }
  }, [isCollapsed])

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
  }

  const displayName =
    auth.user?.fullName || auth.user?.username || auth.user?.email || 'User'
  const displayEmail = auth.user?.email || ''

  const handleLogout = async () => {
    try {
      await auth.logoutUser()
    } finally {
      setIsOpen(false)
      navigate('/login', { replace: true })
    }
  }

  return (
    <div ref={containerRef} className="relative mt-auto border-t border-border p-2">
      <button
        type="button"
        onClick={() => {
          if (isCollapsed) {
            onRequestExpand?.()
            return
          }
          setIsOpen(!isOpen)
        }}
        className="flex w-full items-center rounded-lg px-2 py-1 text-left transition-colors hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="User profile"
        aria-expanded={isOpen}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
          <User size={20} />
        </div>

        <div
          className={cn(
            'flex flex-1 flex-col overflow-hidden transition-all duration-300 ease-in-out',
            isCollapsed ? 'max-w-0 opacity-0' : 'ml-2 max-w-[200px] opacity-100',
          )}
        >
          <span className="truncate text-sm font-medium text-foreground">
            {displayName}
          </span>
          <span className="truncate text-xs text-muted-foreground">{displayEmail}</span>
        </div>

        {!isCollapsed && (
          <MoreVertical size={16} className="shrink-0 text-muted-foreground" />
        )}
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-2 right-2 z-50 mb-2 min-w-[228px] rounded-xl border border-border/80 bg-popover/95 p-1.5 shadow-xl backdrop-blur">
          <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {t('topbar.source')}
          </div>
          <Link
            to={ROUTES.app.root}
            className={menuItemStyle}
            onClick={() => setIsOpen(false)}
          >
            <User size={16} strokeWidth={ICON_STROKE} />
            {t('topbar.profile')}
          </Link>
          <Link
            to={ROUTES.app.settings}
            className={menuItemStyle}
            onClick={() => setIsOpen(false)}
          >
            <Settings size={16} strokeWidth={ICON_STROKE} />
            {t('nav.settings')}
          </Link>

          <div className="my-1 border-t border-border/80" />

          <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {t('topbar.appearance')}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className={cn(menuItemStyle, 'w-full justify-between')}
          >
            <div className="flex items-center gap-3">
              {theme === 'light' ? (
                <Sun size={16} strokeWidth={ICON_STROKE} />
              ) : (
                <Moon size={16} strokeWidth={ICON_STROKE} />
              )}
              <span>{t('topbar.theme')}</span>
            </div>
            <span className="text-[11px] text-muted-foreground capitalize">{theme}</span>
          </button>

          <div
            className={cn(
              menuItemStyle,
              'w-full justify-between cursor-default hover:bg-transparent',
            )}
          >
            <div className="flex items-center gap-3">
              <Languages size={16} strokeWidth={ICON_STROKE} />
              <span>{t('topbar.language')}</span>
            </div>
            <div className="inline-flex items-center gap-1 text-xs">
              <button
                type="button"
                onClick={() => changeLanguage('en')}
                className={cn(
                  'rounded px-1.5 py-0.5 transition',
                  i18n.language === 'en'
                    ? 'font-semibold text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                EN
              </button>
              <span className="text-muted-foreground/50">/</span>
              <button
                type="button"
                onClick={() => changeLanguage('mn')}
                className={cn(
                  'rounded px-1.5 py-0.5 transition',
                  i18n.language === 'mn'
                    ? 'font-semibold text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                MN
              </button>
            </div>
          </div>

          <div className="my-1 border-t border-border/80" />

          <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {t('topbar.subscription')}
          </div>
          <button
            className={cn(menuItemStyle, 'w-full justify-start')}
            onClick={() => setIsOpen(false)}
          >
            <CreditCard size={16} strokeWidth={ICON_STROKE} />
            {t('upgrade.upgradeBtn')}
          </button>

          <div className="my-1 border-t border-border/80" />

          <button
            type="button"
            className={cn(
              menuItemStyle,
              'w-full justify-start border-none bg-transparent text-destructive cursor-pointer hover:bg-destructive/10',
            )}
            onClick={() => {
              void handleLogout()
            }}
            disabled={auth.isLogoutPending}
          >
            <LogOut size={16} strokeWidth={ICON_STROKE} />
            {auth.isLogoutPending ? t('auth.actions.signingOut') : t('topbar.logout')}
          </button>
        </div>
      )}
    </div>
  )
})

const menuItemStyle =
  'flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm text-foreground no-underline transition-colors hover:bg-muted cursor-pointer bg-transparent border-none'
